import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Edit, Home, TrendingUp, DollarSign, Building, Users } from 'lucide-react';
import { toast } from 'sonner';

const Dashboard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [calculations, setCalculations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [planRes, calcRes] = await Promise.all([
        axios.get(`${API}/farm-plans/${id}`),
        axios.get(`${API}/farm-plans/${id}/calculations`)
      ]);
      setPlan(planRes.data);
      setCalculations(calcRes.data);
      
      // Automatically send email to Jim for review (only once)
      if (!emailSent) {
        sendEmailToReviewer(planRes.data);
      }
    } catch (error) {
      toast.error('Failed to load plan data');
    } finally {
      setLoading(false);
    }
  };

  const sendEmailToReviewer = async (planData) => {
    try {
      await axios.post(`${API}/farm-plans/${id}/send-email`, {
        client_name: 'Jim Chamberlain',
        client_email: 'Jim@chamberlainexperiorgroup.com'
      });
      setEmailSent(true);
      toast.success('Report sent to your email for review');
    } catch (error) {
      console.error('Failed to send review email:', error);
      // Don't show error to user - this is a background operation
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F9F8F4] via-[#EEE8DC] to-[#F9F8F4] flex items-center justify-center">
        <div className="text-2xl text-[#2E4F3E] font-semibold">Loading...</div>
      </div>
    );
  }

  if (!plan || !calculations) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F9F8F4] via-[#EEE8DC] to-[#F9F8F4] flex items-center justify-center">
        <div className="text-2xl text-[#2E4F3E] font-semibold">Plan not found</div>
      </div>
    );
  }

  // Prepare chart data
  const assetDistributionData = [
    { name: 'Liquid Assets', value: calculations.total_liquid_assets, color: '#2E4F3E' },
    { name: 'Non-Liquid Assets', value: calculations.total_non_liquid_assets, color: '#C87941' },
    { name: 'Business Fixed Assets', value: calculations.total_fixed_assets, color: '#7A9C8A' }
  ].filter(item => item.value > 0);

  const netWorthBreakdown = [
    { category: 'Personal Assets', amount: calculations.total_personal_assets },
    { category: 'Business Assets', amount: calculations.total_business_assets },
    { category: 'Total Net Worth', amount: calculations.total_net_worth }
  ];

  const landAssetsData = [
    { name: 'Land', value: plan.business_assets.land_value },
    { name: 'Livestock', value: plan.business_assets.livestock_value },
    { name: 'Equipment', value: plan.business_assets.equipment_value }
  ].filter(item => item.value > 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F9F8F4] via-[#EEE8DC] to-[#F9F8F4] py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-bold text-[#2E4F3E] mb-2" data-testid="dashboard-title">
              {plan.business_name}
            </h1>
            <p className="text-lg text-[#5A6B5E]">Owner: {plan.owner_name}</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="border-[#2E4F3E] text-[#2E4F3E] hover:bg-[#2E4F3E] hover:text-white"
              data-testid="home-btn"
            >
              <Home className="mr-2 w-4 h-4" />
              Home
            </Button>
            <Button
              onClick={() => navigate(`/edit/${id}`)}
              variant="outline"
              className="border-[#C87941] text-[#C87941] hover:bg-[#C87941] hover:text-white"
              data-testid="edit-plan-btn"
            >
              <Edit className="mr-2 w-4 h-4" />
              Edit Plan
            </Button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="border-2 border-[#2E4F3E]/20 shadow-lg bg-white" data-testid="metric-net-worth">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#5A6B5E] font-medium">Total Net Worth</p>
                  <p className="text-3xl font-bold text-[#2E4F3E] mt-2">
                    ${calculations.total_net_worth.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </p>
                </div>
                <div className="bg-[#2E4F3E]/10 p-3 rounded-xl">
                  <DollarSign className="w-8 h-8 text-[#2E4F3E]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-[#2E4F3E]/20 shadow-lg bg-white" data-testid="metric-personal-assets">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#5A6B5E] font-medium">Personal Assets</p>
                  <p className="text-3xl font-bold text-[#2E4F3E] mt-2">
                    ${calculations.total_personal_assets.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </p>
                </div>
                <div className="bg-[#C87941]/10 p-3 rounded-xl">
                  <Home className="w-8 h-8 text-[#C87941]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-[#2E4F3E]/20 shadow-lg bg-white" data-testid="metric-business-assets">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#5A6B5E] font-medium">Business Assets</p>
                  <p className="text-3xl font-bold text-[#2E4F3E] mt-2">
                    ${calculations.total_business_assets.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </p>
                </div>
                <div className="bg-[#2E4F3E]/10 p-3 rounded-xl">
                  <Building className="w-8 h-8 text-[#2E4F3E]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-[#2E4F3E]/20 shadow-lg bg-white" data-testid="metric-employees">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#5A6B5E] font-medium">Total Employees</p>
                  <p className="text-3xl font-bold text-[#2E4F3E] mt-2">
                    {(plan.employee_info?.full_time_count || 0) + (plan.employee_info?.part_time_count || 0)}
                  </p>
                </div>
                <div className="bg-[#C87941]/10 p-3 rounded-xl">
                  <Users className="w-8 h-8 text-[#C87941]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Asset Distribution Pie Chart */}
          {assetDistributionData.length > 0 && (
            <Card className="border-2 border-[#2E4F3E]/20 shadow-lg bg-white" data-testid="chart-asset-distribution">
              <CardHeader>
                <CardTitle className="text-2xl text-[#2E4F3E]">Asset Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={assetDistributionData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {assetDistributionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Net Worth Breakdown Bar Chart */}
          <Card className="border-2 border-[#2E4F3E]/20 shadow-lg bg-white" data-testid="chart-net-worth">
            <CardHeader>
              <CardTitle className="text-2xl text-[#2E4F3E]">Net Worth Breakdown</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={netWorthBreakdown}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                  <XAxis dataKey="category" stroke="#5A6B5E" />
                  <YAxis stroke="#5A6B5E" />
                  <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                  <Bar dataKey="amount" fill="#2E4F3E" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Land Assets Distribution */}
          {landAssetsData.length > 0 && (
            <Card className="border-2 border-[#2E4F3E]/20 shadow-lg bg-white" data-testid="chart-land-assets">
              <CardHeader>
                <CardTitle className="text-2xl text-[#2E4F3E]">Farm Assets Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={landAssetsData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      <Cell fill="#2E4F3E" />
                      <Cell fill="#C87941" />
                      <Cell fill="#7A9C8A" />
                    </Pie>
                    <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Succession Planning Status */}
          <Card className="border-2 border-[#2E4F3E]/20 shadow-lg bg-white" data-testid="succession-status">
            <CardHeader>
              <CardTitle className="text-2xl text-[#2E4F3E]">Succession Planning Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-[#F9F8F4] rounded-lg">
                  <span className="font-medium text-[#2E4F3E]">Has Will</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${plan.succession_plan?.has_will ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {plan.succession_plan?.has_will ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[#F9F8F4] rounded-lg">
                  <span className="font-medium text-[#2E4F3E]">Buy/Sell Agreement</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${plan.succession_plan?.has_buy_sell ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {plan.succession_plan?.has_buy_sell ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[#F9F8F4] rounded-lg">
                  <span className="font-medium text-[#2E4F3E]">Succession Plan</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${plan.succession_plan?.has_succession_plan ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {plan.succession_plan?.has_succession_plan ? 'Yes' : 'No'}
                  </span>
                </div>
                {plan.succession_plan?.retirement_plan && (
                  <div className="p-3 bg-[#C87941]/10 rounded-lg">
                    <p className="text-sm font-semibold text-[#2E4F3E] mb-1">Retirement Plan:</p>
                    <p className="text-[#5A6B5E]">{plan.succession_plan.retirement_plan}</p>
                  </div>
                )}
                {plan.succession_plan?.core_values && (
                  <div className="p-3 bg-[#2E4F3E]/10 rounded-lg">
                    <p className="text-sm font-semibold text-[#2E4F3E] mb-1">Core Values:</p>
                    <p className="text-[#5A6B5E]">{plan.succession_plan.core_values}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Family Members */}
        {plan.family_members && plan.family_members.length > 0 && plan.family_members[0].name && (
          <Card className="border-2 border-[#2E4F3E]/20 shadow-lg bg-white mb-8" data-testid="family-members-section">
            <CardHeader>
              <CardTitle className="text-2xl text-[#2E4F3E]">Family Members</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-[#2E4F3E]/20">
                      <th className="text-left p-3 text-[#2E4F3E] font-semibold">Name</th>
                      <th className="text-left p-3 text-[#2E4F3E] font-semibold">Relationship</th>
                      <th className="text-left p-3 text-[#2E4F3E] font-semibold">Age</th>
                      <th className="text-left p-3 text-[#2E4F3E] font-semibold">Position</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.family_members.map((member, index) => (
                      member.name && (
                        <tr key={index} className="border-b border-[#2E4F3E]/10">
                          <td className="p-3 text-[#5A6B5E]">{member.name}</td>
                          <td className="p-3 text-[#5A6B5E]">{member.relationship}</td>
                          <td className="p-3 text-[#5A6B5E]">{member.age || 'N/A'}</td>
                          <td className="p-3 text-[#5A6B5E]">{member.position || 'N/A'}</td>
                        </tr>
                      )
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Dashboard;